import React, { useContext, useEffect, useMemo, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import CompanyService from "../../../services/CompanyService";
import AccountService, {
  SignatureResponse
} from "../../../services/AccountService";
import ToastContext from "../../shared/bootstrap/Toast";
import FileService from "../../../services/FileService";
import Loading from "../../shared/components/Loading";
import { SafeHtml } from "../../../components/SafeHtml";

export const SignData = () => {
  const { showToast } = useContext(ToastContext);
  const [changingData, setChangingData] = useState(false);
  const [dataText, setDataText] = useState({ signature: "" });
  const [dataImg, setDataImg] = useState<SignatureResponse["item"]>();

  const [imageChanged, setImageChanged] = useState(false);
  const [fileUploaded, setFileUploaded] = useState<File>();
  const [loading, setLoading] = useState(true);

  const showSignatureImage = useMemo(
    () => Boolean(dataImg?.Content.length),
    [dataImg?.Content.length]
  );
  const imgSrc = useMemo(
    () =>
      dataImg?.Content.replace(
        "cid:",
        "https://cdn.intiza.com/mailsignature/"
      ).replace(">", ' style="max-width: 1200px; width: 100%; height: auto;">'),
    [dataImg?.Content]
  );

  const handleChangeData = () => setChangingData((p) => !p);

  const handleInputChange = ({
    target: { name, value }
  }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDataText((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleFileInputChange = (file: File | null) => {
    if (!file) {
      return;
    }
    setImageChanged(true);
    setFileUploaded(file);
  };

  const saveSignature = async () => {
    await AccountService.setAccountSignature({
      signature: dataText.signature
    }).finally(handleChangeData);
  };

  const deleteSignature = async () => {
    const result = await AccountService.deleteAccountSignature();
    if (result instanceof Error) {
      showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }
    setDataImg(undefined);
  };

  const uploadFile = async () => {
    try {
      setLoading(true);
      if (fileUploaded) {
        const result = await FileService.upload(fileUploaded);
        if (result instanceof Error) {
          showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
          return result;
        }
        await AccountService.setAccountSignatureImage({
          id: result.id
        }).finally(() => {
          loadDataSignature();
          setImageChanged(false);
          setLoading(false);
        });
      }
    } catch (error) {
      showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setLoading(false);
    }
  };

  const cancelUploadFile = () => {
    loadDataSignature();
    setImageChanged(false);
  };

  const loadDataSignature = async () => {
    setLoading(true);
    const result = await AccountService.getAccountSignature();
    const resultText = await AccountService.getAccountSignatureText();

    if (result instanceof Error || resultText instanceof Error) {
      showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setLoading(false);
      return;
    }
    setDataImg(result.item);
    setDataText({ signature: resultText.signature });
    setLoading(false);
  };

  useEffect(() => {
    loadDataSignature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <Loading />
  ) : (
    <div className="card-body">
      <div className="d-flex align-items-center show-child-on-hover gap-3 mb-2">
        <h5 className="pb-1">
          {TranslationService.translate.PreloadedSignature.replace(
            "{1}",
            CompanyService.getCompanyName()
          )}
        </h5>
        {!changingData ? (
          <i
            className="far fa-pen pointer display-when-hovering-parent"
            onClick={handleChangeData}
          ></i>
        ) : (
          <i
            className="fa-fw fa-solid fa-times text-danger pointer"
            onClick={handleChangeData}
          />
        )}
      </div>

      {changingData ? (
        <div className="col">
          <div className="col mb-3">
            <label className="font-weight-normal col-form-label">
              {TranslationService.translate.DefaultEmailSignatureText}
            </label>
            <textarea
              className="form-control"
              onChange={handleInputChange}
              name="signature"
              defaultValue={dataText.signature}
              style={{ resize: "none" }}
              rows={5}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
              translate="no"
            />
          </div>
        </div>
      ) : (
        <div className="col">
          <div className="col mb-3">
            <label className="font-weight-normal col-form-label">
              {TranslationService.translate.DefaultEmailSignatureText}
            </label>
            <pre className="signatureFont">{`${dataText.signature}`}</pre>
          </div>
        </div>
      )}

      <div className="mb-2 d-flex justify-content-end">
        {!changingData ? null : (
          <div className="d-flex gap-2">
            <button
              className="col btn btn-primary mt-2"
              onClick={saveSignature}
            >
              {TranslationService.translate.Save}
            </button>
          </div>
        )}
      </div>

      {showSignatureImage ? (
        <div className="img-signature-wrapper">
          <label className="signature-label">
            {TranslationService.translate.DefaultEmailSignatureImage}
          </label>
          <div className="img-signature-container">
            {imgSrc && (
              <>
                <div className="img-signature-content">
                  <SafeHtml html={imgSrc} />
                </div>
                <div className="clean-img-thumb" onClick={deleteSignature}>
                  <i className="fa fa-times" />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-2">
            <label className="font-weight-normal col-form-label">
              {TranslationService.translate.DefaultEmailSignatureImage}
            </label>
            <input
              type="file"
              className="form-control"
              accept=".jpg,.jpeg,.png"
              onChange={(e) =>
                handleFileInputChange(e.target.files?.[0] ?? null)
              }
            />
          </div>
          {imageChanged ? (
            <div className="mb-2 d-flex justify-content-end">
              <div className="d-flex gap-2">
                <button
                  className="col btn btn-primary mt-2"
                  onClick={uploadFile}
                >
                  {TranslationService.translate.Save}
                </button>
                <button
                  className=" col btn btn-primary mt-2"
                  onClick={cancelUploadFile}
                >
                  {TranslationService.translate.Cancel}
                </button>
              </div>
            </div>
          ) : null}
          <hr />
        </>
      )}
    </div>
  );
};
